import { useRef, useState } from 'react';
import { Button, HeroControlItem, IconButton, Input } from '@wundo/ui';
import { Plus, Trash2 } from 'lucide-react';
import { MAX_MEETING_DOCUMENT_BYTES } from '@wundo/shared/attachments.ts';
import { formatFileSize } from '@wundo/shared/fileSize.ts';
import type {
  MeetingPageDocument,
  MeetingPageDocumentStatus,
} from '@wundo/shared/meetingPage.ts';
import { formatDayAndMonth } from '@/components/agenda/meetingFormat.ts';
import { THREAD_DOCUMENT_MIME_TYPES } from '@/lib/meetingPage.ts';
import { m } from '@/paraglide/messages.js';

/** Why a file was not taken, in terms this panel can put into words. */
type Refusal = 'type' | 'size';

export interface MeetingDocumentListProps {
  documents: MeetingPageDocument[];
  /** Whether a file is on its way up right now. */
  uploading: boolean;
  /** Whether the last one that went up came back refused. */
  uploadFailed: boolean;
  onAdd: (file: File) => void;
  onDelete: (document: MeetingPageDocument) => void;
}

/**
 * The documents of this conversation, and the way to add one.
 *
 * A document hangs on the conversation rather than on a meeting, so this list
 * is the same on every meeting in the series — which is also why a row says
 * when it arrived: two versions of the same file are told apart by their date.
 *
 * A file is refused here before it is sent wherever the browser already knows
 * enough to say so, and the answer takes the place of nothing: the line under
 * the list is the only thing that appears, because a dialog for "that file is
 * too big" would be a second click for a sentence.
 */
export function MeetingDocumentList({
  documents,
  uploading,
  uploadFailed,
  onAdd,
  onDelete,
}: MeetingDocumentListProps) {
  const field = useRef<HTMLInputElement>(null);
  const [refused, setRefused] = useState<Refusal | null>(null);

  const choose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    /*
     * Emptied straight away: a file field that still holds the last choice
     * reports no change when the same file is picked again, and picking the
     * same file again is exactly what somebody does after a failure.
     */
    event.target.value = '';
    if (!file) return;

    if (!THREAD_DOCUMENT_MIME_TYPES.includes(file.type)) {
      setRefused('type');
      return;
    }
    if (file.size > MAX_MEETING_DOCUMENT_BYTES) {
      setRefused('size');
      return;
    }

    setRefused(null);
    onAdd(file);
  };

  const problem = refused ?? (uploadFailed ? 'failed' : null);

  return (
    <>
      {documents.map((item) => (
        <HeroControlItem
          key={item.id}
          name={
            /*
             * The name is the way to the file itself — there is nothing else a
             * document could be opened by. Downloaded through the backend, the
             * same door every attachment leaves by.
             */
            <a
              href={item.url}
              target='_blank'
              rel='noreferrer'
              className='hover:underline'
            >
              {item.name}
            </a>
          }
          meta={`${formatDayAndMonth(new Date(item.created_at))} · ${statusText(item.status)}`}
          action={
            <IconButton
              size='sm'
              variant='danger'
              aria-label={m.meeting_sources_document_delete()}
              onClick={() => onDelete(item)}
            >
              <Trash2 className='size-4' aria-hidden='true' />
            </IconButton>
          }
        />
      ))}

      {/*
        Hidden, and opened by the button under it. The field's own control is
        the one thing in this design system that cannot be styled, and "No file
        chosen" says nothing inside a panel that lists the files already there.
      */}
      <Input
        ref={field}
        type='file'
        accept={THREAD_DOCUMENT_MIME_TYPES.join(',')}
        className='hidden'
        aria-label={m.meeting_sources_document_add()}
        onChange={choose}
      />

      <Button
        size='sm'
        variant='ghost'
        className='mt-1.5 w-full justify-start'
        loading={uploading}
        disabled={uploading}
        onClick={() => field.current?.click()}
      >
        <Plus aria-hidden='true' className='size-3.5' />
        {m.meeting_sources_document_add()}
      </Button>

      {problem && (
        <p role='alert' className='mt-1.5 text-sm text-danger'>
          {problemText(problem)}
        </p>
      )}
    </>
  );
}

/** Where Wundo stands with this file, in the two or three words a row has. */
function statusText(status: MeetingPageDocumentStatus): string {
  if (status === 'reading') return m.meeting_sources_document_reading();
  if (status === 'failed') return m.meeting_sources_document_failed();
  return m.meeting_sources_document_ready();
}

function problemText(problem: Refusal | 'failed'): string {
  if (problem === 'type') return m.meeting_sources_document_type();
  if (problem === 'size') {
    return m.meeting_sources_document_size({
      size: formatFileSize(MAX_MEETING_DOCUMENT_BYTES),
    });
  }
  return m.meeting_sources_document_upload_failed();
}
